import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supplierAPI } from '../api';
import { SupplierBasicInfo, SupplierDetailForm, SupplierImages } from '../components/features';
import { TFSLoader, ErrorState } from '../components/ui';
import '../styles/SupplierDetail.css';

const SupplierDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    supplier_name: '',
    status: 'completed',
    instructions: '',
    isfewshot: false,
    process_all_pages: false
  });

  useEffect(() => {
    fetchSupplierDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchSupplierDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await supplierAPI.getSupplierById(id);
      setSupplier(data);
      setFormData({
        supplier_name: data.supplier_name || '',
        status: data.status || 'completed',
        instructions: data.instructions || '',
        isfewshot: data.isfewshot || false,
        process_all_pages: data.process_all_pages || false
      });
    } catch (error) {
      console.error('Error fetching supplier details:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const updatedData = await supplierAPI.updateSupplier(id, formData);
      setSupplier(updatedData);
      alert('✅ Supplier updated successfully!');
    } catch (error) {
      console.error('Error updating supplier:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <TFSLoader />;
  }

  if (error) {
    return (
      <div className="supplier-detail">
        <ErrorState 
          title="Error Loading Supplier"
          message={error}
          onAction={() => navigate('/suppliers')}
          actionLabel="Back to Suppliers"
        />
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="supplier-detail">
        <ErrorState 
          title="Supplier Not Found"
          message="The supplier you're looking for doesn't exist."
          onAction={() => navigate('/suppliers')}
          actionLabel="Back to Suppliers"
        />
      </div>
    );
  }

  return (
    <div className="supplier-detail">
      <div className="detail-header">
        <button className="btn-back" onClick={() => navigate('/suppliers')}>
          ← Back to Suppliers
        </button>
        <div className="detail-title">
          <div className="supplier-avatar-large">
            {supplier.supplier_name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h1>{supplier.supplier_name}</h1>
            <span className={`status-badge ${supplier.status.toLowerCase()}`}>
              {supplier.status}
            </span>
          </div>
        </div>
      </div>

      <div className="detail-grid">
        <SupplierBasicInfo supplier={supplier} />

        <SupplierDetailForm 
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/suppliers')}
          saving={saving}
        />

        <SupplierImages images={supplier.annotated_images} />
      </div>
    </div>
  );
};

export default SupplierDetail;
